import {atomWithoutDefault} from '@raison/toolbox';
import {Dispatch} from '@reduxjs/toolkit';
import {useAtomValue} from 'jotai';
import {SessionExpiredMsg} from '~/entities/dialogs';
import {appLocalStorage, appSessionStorage} from '~/shared/persistence';
import {LoginResponseData} from '../../api/login';
import {logoutWithToken} from '../../api/logoutWithToken';

type LogoutAndRestartOptions = {
    withToken?: boolean;
    state?: unknown;
};

const logoutAndRestart =
    (destination: string, {withToken = true, state = {disableBiometryAutorun: true}}: LogoutAndRestartOptions = {}) =>
    (_dispatch: Dispatch, _: unknown, {httpPublic}: AppServices) => {
        const token = withToken && appSessionStorage.authToken.get();
        clearUserSession();
        if (token) {
            logoutWithToken(httpPublic, token);
        }
        window.restartAppAt(destination, state);
    };

export function clearUserSession() {
    appSessionStorage.authToken.remove();
    appSessionStorage.refreshToken.remove();
    appSessionStorage.authRoles.remove();
    appSessionStorage.hPass.remove();
}

type LogoutCallback = {
    onConfirm?: () => void;
};

export const logout =
    ({onConfirm}: LogoutCallback = {}) =>
    (dispatch: AppDispatch, getState: () => AppModel) => {
        const [isOpen] = SessionExpiredMsg.select(getState());
        if (isOpen) {
            return;
        }

        dispatch(
            SessionExpiredMsg.open({
                onConfirm: () => {
                    dispatch(logoutNow());
                    onConfirm?.();
                },
            })
        );
    };

export const logoutNow = () => logoutAndRestart('/login', {withToken: false});
export const logoutNowEverywhere = () => logoutAndRestart('/login');
export const passwordChanged = () => logoutAndRestart('/successfully-changed');

export const logoutCompletely = () => {
    appLocalStorage.authStatus.remove();
    appLocalStorage.givenName.remove();
    appLocalStorage.username.remove();
    return logoutAndRestart('/', {withToken: true});
};

export const handleAuthDataReceived = (result: LoginResponseData) => () => {
    appSessionStorage.userId.set(`${result.user.id}`);
    appSessionStorage.authToken.set(result.access_token);
    appSessionStorage.refreshToken.set(result.refresh_token);
    appSessionStorage.authRoles.set(result.user.roles);
    appSessionStorage.authStatus.set(result.user.status);
    appSessionStorage.addressStatus.set(result.user.address_status);
};

export const Logout$ = atomWithoutDefault<(cb?: LogoutCallback) => void>('Logout$');
export const LogoutNow$ = atomWithoutDefault<() => void>('LogoutImmediately$');
export const LogoutNowEverywhere$ = atomWithoutDefault<() => void>('LogoutImmediatelyEverywhere$');

export const useLogout = () => useAtomValue(Logout$);
